import React from 'react';
import { ArrowRight, Truck, CheckCircle, Clock, MapPin, Shield, Car } from 'lucide-react';
import SEO from '../components/SEO';

const RVBoatStorage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'Interskate Self Storage – Vehicle Storage in Lewisville, TX',
    url: 'https://www.interskateselfstorage.com/rv-boat-storage-lewisville',
    telephone: '(469) 251-6255',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1303 S Stemmons Fwy',
      addressLocality: 'Lewisville',
      addressRegion: 'TX',
      postalCode: '75067',
      addressCountry: 'US'
    },
    description: 'Outdoor vehicle storage in Lewisville, TX on the I-35E service road. Secure parking for cars, trucks, and commercial vehicles. Electronic gate. Month-to-month.',
    priceRange: '$'
  };

  const vehicleTypes = [
    { icon: <Car className="h-8 w-8 text-green-600" />, label: 'Classic & Collector Cars' },
    { icon: <Truck className="h-8 w-8 text-green-600" />, label: 'Extra Trucks & Vans' },
    { icon: <Truck className="h-8 w-8 text-green-600" />, label: 'Commercial Work Vehicles' },
    { icon: <Car className="h-8 w-8 text-green-600" />, label: 'Non-Running Vehicles' }
  ];

  const benefits = [
    'Right off I-35E — easy access from anywhere in DFW',
    'Wide drive aisles for trucks and vans',
    'Electronic gate access 6 AM – 10 PM, 7 days a week',
    '24/7 video surveillance',
    'Month-to-month — no long-term commitment',
    'No HOA parking restrictions',
    'Affordable rates compared to indoor parking facilities',
    'Online rentals — reserve without visiting'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Vehicle Storage in Lewisville, TX | Interskate Self Storage | I-35E"
        description="Secure outdoor vehicle storage in Lewisville, TX on I-35E. Electronic gate, 24/7 surveillance, month-to-month. Store extra cars, trucks, or commercial vehicles near DFW."
        canonical="/rv-boat-storage-lewisville"
        structuredData={structuredData}
      />

      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="rv-storage-title">
            Vehicle Storage in Lewisville, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Secure outdoor vehicle parking on the I-35E service road — the most convenient spot in North DFW
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Need a secure place to store an extra vehicle? Interskate Self Storage offers outdoor vehicle parking right on the I-35E service road in Lewisville — with electronic gate access and 24/7 video surveillance to keep your vehicle protected.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you have a collector car, an extra work truck, or a commercial vehicle you don't drive every day, month-to-month parking lets you store it here without a long commitment.
          </p>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Store
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Secure parking for personal and commercial vehicles
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {vehicleTypes.map((type, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="flex justify-center mb-3">{type.icon}</div>
                <p className="text-gray-800 font-semibold">{type.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Lewisville Vehicle Owners Choose Interskate
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Central DFW Location</h3>
              <p className="text-gray-600">
                Right on S. Stemmons Fwy (I-35E) in Lewisville — accessible from Denton, Flower Mound, Carrollton, and all of North DFW.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure Facility</h3>
              <p className="text-gray-600">
                Electronic gate access plus 24/7 video surveillance. Your vehicle is protected whether you're in town or away.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Clock className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Flexible Access</h3>
              <p className="text-gray-600">
                Gate open 6 AM – 10 PM every day. Pick up or drop off your vehicle whenever you need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Why Store Your Vehicle Here
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serving Area */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Convenient for All of North DFW</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Our Lewisville facility is ideally positioned for vehicle owners across the metroplex. If you live in Flower Mound, Highland Village, Denton, Carrollton, or Coppell — we're your most accessible outdoor vehicle storage option.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Stop worrying about HOA complaints or running out of driveway space. Keep your extra vehicle safe and out of the way in a secure monitored facility that's easy to reach on I-35E.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Reserve Your Parking Space Today</h2>
          <p className="text-xl text-green-100 mb-8">
            Check availability and rent online in minutes — no office visit required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://interskate.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="rent-now-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-green-700 transition-colors"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RVBoatStorage;
